import { bffJson } from "@/shared/api/client";
import { requireAdvisor } from "@/shared/api/identity";

export function activeAtInstant(now: Date = new Date()): string {
  return now.toISOString().replace("Z", "000Z");
}

export interface ClientLinkFilter {
  ownerCorporationId: string | null;
  individualId?: string;
  corporationId?: string;
  financialAdvisorId?: string;
  activeAt?: string | null;
}

export function clientLinkQuery(filter: ClientLinkFilter): string {
  const params = new URLSearchParams();
  if (filter.ownerCorporationId) params.set("owner_corporation_id", filter.ownerCorporationId);
  if (filter.individualId) params.set("individual_id", filter.individualId);
  if (filter.corporationId) params.set("corporation_id", filter.corporationId);
  if (filter.financialAdvisorId) {
    params.set("primary_financial_advisor_id", filter.financialAdvisorId);
  }
  if (filter.activeAt !== null) params.set("active_at", filter.activeAt ?? activeAtInstant());
  return params.toString();
}

export async function linkOwnerCorporationId(): Promise<string | null> {
  return (await requireAdvisor()).corporationId;
}

interface IndividualClientRow {
  id?: string;
  individual_id?: string;
}

const RESOLVE_TTL_MS = 5 * 60_000;
type CacheEntry = { icId: string | null; expires: number };
const cache = new Map<string, CacheEntry>();

export function rememberIndividualClientId(individualId: string, icId: string): void {
  cache.set(individualId, { icId, expires: Date.now() + RESOLVE_TTL_MS });
}

export async function resolveIndividualClientId(individualId: string): Promise<string | null> {
  if (!individualId) return null;
  const now = Date.now();
  const hit = cache.get(individualId);
  if (hit && hit.expires > now) return hit.icId;

  const json = await bffJson<unknown>(
    `v1/individual-client?${clientLinkQuery({
      ownerCorporationId: await linkOwnerCorporationId(),
      individualId,
    })}`,
  );
  const rows = Array.isArray(json) ? (json as IndividualClientRow[]) : [];
  const matchingRows = rows.filter(
    (row) => typeof row.id === "string" && row.id !== "" && row.individual_id === individualId,
  );
  if (matchingRows.length > 1) {
    console.warn(
      `individual-client: individual ${individualId} has ${matchingRows.length} active client rows in the firm — using the first`,
    );
  }
  const icId = matchingRows[0]?.id ?? null;
  cache.set(individualId, { icId, expires: now + RESOLVE_TTL_MS });
  return icId;
}
